import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { combineLatest, map, shareReplay } from 'rxjs';
import { PlansService } from '../../../../core/services/plans.service';
import { AsyncPipe } from '@angular/common';
import { Plan } from '../../../../shared/models/plans.models';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-change-plan-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    AsyncPipe,
  ],
  templateUrl: './change-plan-dialog.component.html',
  styleUrl: './change-plan-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePlanDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<ChangePlanDialogComponent>);

  readonly data = inject<DialogData & { currentPlan: Plan }>(MAT_DIALOG_DATA);

  private plansService = inject(PlansService);

  private fb = inject(FormBuilder);

  form = this.fb.nonNullable.group({
    currentPlan: [{ value: '', disabled: true }], // Just for the template
    plan: [null, [Validators.required, this.planValidator(this.data.currentPlan.id.toString())]],
  });

  planSearchText = signal('');

  plans$ = this.plansService.getAll().pipe(shareReplay(1));

  displayedPlans$ = combineLatest({
    plans: this.plans$,
    search: toObservable(this.planSearchText),
  }).pipe(
    map(({ plans, search }) =>
      plans
        .filter((p) => p.name.includes(search))
        .map((p) => ({ label: p.name, value: p.id.toString() })),
    ),
  );

  ngOnInit(): void {
    this.form.controls.currentPlan.setValue(this.data.currentPlan.name);
  }

  planValidator(currentPlanId: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const planId = control.value;

      if (planId === currentPlanId) {
        return { forbidden: true };
      }

      return null;
    };
  }

  submit(): void {
    if (this.form.invalid) return;

    this.dialogRef.close(this.form.getRawValue());
  }

  close(): void {
    this.dialogRef.close();
  }
}
